import React, { useEffect, useState } from 'react';
import { getSystemHealth, type SystemHealthSnapshot } from '../../services/admin';

export const SystemStatusCard: React.FC = () => {
  const [health, setHealth] = useState<SystemHealthSnapshot | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchHealth = async () => {
      setLoading(true);
      try {
        const snapshot = await getSystemHealth();
        if (!cancelled) {
          setHealth(snapshot);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Unable to reach API health endpoint');
          setHealth(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchHealth();
    const id = setInterval(fetchHealth, 15000);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const statusBadge = () => {
    if (!health) return null;
    const ok = health.status === 'ok';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`}
      >
        {ok ? 'All systems nominal' : 'Degraded'}
      </span>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">System status</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Quick view of API, database, and Stripe configuration for this environment.
          </p>
        </div>
        {statusBadge()}
      </div>

      {loading && (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Checking backend health   </p>
      )}
      {!loading && error && <p className="mt-3 text-xs text-red-600">{error}</p>}

      {!loading && health && (
        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-600 dark:text-slate-400">
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-200">API</p>
            <p className="mt-0.5">Env: {health.env.nodeEnv}</p>
            <p className="mt-0.5">Port: {health.env.port}</p>
          </div>
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-200">Database</p>
            <p className="mt-0.5">Connected: {health.mongo.connected ? 'Yes' : 'No'}</p>
            <p className="mt-0.5">State code: {health.mongo.state}</p>
          </div>
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-200">Stripe</p>
            <p className="mt-0.5">
              Configured: {health.stripe.configured ? 'Yes' : 'No (mock / disabled)'}
            </p>
            <p className="mt-0.5">
              Secret key: {health.stripe.secretKeyPresent ? 'Present' : 'Missing'}
            </p>
            <p className="mt-0.5">
              Webhook secret: {health.stripe.webhookSecretPresent ? 'Present' : 'Missing'}
            </p>
          </div>
          <div>
            <p className="font-medium text-slate-800 dark:text-slate-200">Uptime</p>
            <p className="mt-0.5">
              {Math.round(health.uptimeSeconds)}s     last check {new Date(
                health.timestamp,
              ).toLocaleTimeString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatusCard;
